// Per-warehouse settings editors shared by the Settings page and the Points
// page.
//
// These four were local to admin-settings.tsx until the Points page pulled
// every time target onto one screen. Copying 500 lines to do that would have
// left two editors that auto-save to the same endpoint and drift apart, so
// they live here instead and both pages import them.
//
// All of them PATCH /settings/<warehouse> themselves and report the saved
// value back through onSaved — the parent holds the rows, the editor owns the
// round trip.
import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";

import { useTranslation } from "../../i18n";
import { useReadOnly } from "../../access/ReadOnly";
import { apiFetch } from "../../api/apiFetch";
import "../../styles/admin-settings.css";

/** Settings columns holding a JSONB {key: milliseconds} map. */
export type TimerMapField =
    | "book_in_po_timers"
    | "scan_sheet_po_timers"
    | "sorter_2_location_timers";

/** Settings columns holding a single millisecond value. */
export type ScalarTimerField =
    // Scoring targets — every one of these decides what a point is worth.
    | "sorter_1_timer_ms"
    | "pick_and_sort_timer_ms"
    | "put_away_timer_ms"
    | "picking_timer_light_ms"
    | "picking_timer_pick_and_sort_ms"
    | "picking_timer_large_ms"
    | "drop_timer_shelf_ms"
    | "drop_timer_forecast_ms"
    | "dispatch_timer_light_ms"
    | "dispatch_timer_pick_and_sort_ms"
    | "dispatch_timer_large_ms"
    | "wipe_timer_ms"
    // UI timings, not targets: these two shape the picker screen's takeover
    // and highlight, and have nothing to do with points.
    | "picking_location_change_ms"
    | "picking_new_location_highlight_ms";

/**
 * The slice of a settings row these editors actually read.
 *
 * Deliberately structural rather than the full WarehouseSettings interface:
 * both pages pass their own richer row type in, and neither has to import the
 * other's.
 */
export interface TimerSettingsRow
    extends Partial<Record<TimerMapField, Record<string, number>>>,
            Partial<Record<ScalarTimerField, number | null>> {
    warehouse: string;
}

// Canonical PO list shown in the Book In + Scan Sheet timer editors. Values
// not in this list but already saved on the backend will still render (via
// the merge in TimerMapEditor).
export const KNOWN_POS = [
    "RED", "YELLOW", "GREEN", "GREEN-HOT", "GREEN-COLD",
    "BLUE", "PINK", "ORANGE", "GREY",
    "Relabel", "BANNED", "STORAGE-DEVICE",
] as const;

// Hardcoded Sorter 2 defaults, scoped per warehouse. Mirrors LOCATION_TIMERS
// in sorter-2-scanner.tsx. The editor pre-populates each warehouse with the
// prefixes for that warehouse only (Newport → NPS, Poland → PLS, others →
// TTS), and uses these values as the placeholder so admins see the current
// effective timer per prefix without having to save anything.
const SORTER_2_DEFAULTS_BY_WAREHOUSE: Record<string, Record<string, number>> = {
    Newport: {
        NPS201: 6000, NPS202: 6000, NPS203: 5500,
        NPS204: 5500, NPS205: 5500, NPS206: 5000,
        // Bay 7 — the NPS207 put wall. 6000 mirrors sorter-2-scanner.tsx; the
        // real target is whatever's saved in sorter_2_location_timers.
        NPS207: 6000,
    },
    Poland: {
        PLS201: 6000, PLS202: 6000, PLS203: 6000, PLS204: 6000,
        PLS205: 5500, PLS206: 5500, PLS207: 5000,
        // Bay 8 — the PLS208 put wall. Mirrors sorter-2-scanner.tsx.
        PLS208: 6000,
    },
    Pembroke: {
        PDS201: 6000, PDS202: 6000,
    },
};
// Fallback for any warehouse not explicitly mapped above (future warehouses).
const SORTER_2_DEFAULT_PREFIXES: Record<string, number> = {
    PDS201: 6000, PDS202: 6000,
};
export const sorter2DefaultsFor = (warehouse: string): Record<string, number> =>
    SORTER_2_DEFAULTS_BY_WAREHOUSE[warehouse] ?? SORTER_2_DEFAULT_PREFIXES;

/** Placeholder shown when a PO / warehouse has no explicit target saved. */
export const DEFAULT_DURATION_MS = 4000;

// Debounced auto-save for the buffer-based editors. Blur already saved,
// but typing a value and navigating away (or using the number-stepper
// arrows and closing the tab) never fired blur, silently dropping the
// change. Values now save themselves shortly after typing stops; blur
// stays as an immediate flush and unmount flushes anything still dirty.
const AUTOSAVE_DEBOUNCE_MS = 800;
export const useDebouncedAutoSave = (saveIfDirty: (key: string) => void) => {
    const timersRef = useRef<Record<string, ReturnType<typeof setTimeout>>>({});
    const saveRef = useRef(saveIfDirty);
    saveRef.current = saveIfDirty;

    const schedule = (key: string) => {
        clearTimeout(timersRef.current[key]);
        timersRef.current[key] = setTimeout(() => saveRef.current(key), AUTOSAVE_DEBOUNCE_MS);
    };
    const flush = (key: string) => {
        clearTimeout(timersRef.current[key]);
        saveRef.current(key);
    };

    useEffect(() => {
        const timers = timersRef.current;
        return () => {
            // Unmount: cancel pending timers and fire a last dirty-check
            // save (fire-and-forget) so leaving the page can't lose the
            // final edit.
            Object.keys(timers).forEach(k => {
                clearTimeout(timers[k]);
                saveRef.current(k);
            });
        };
    }, []);

    return { schedule, flush };
};

const TimerMapEditor = ({
    warehouse,
    field,
    knownKeys,
    initial,
    getPlaceholder,
    onSaved,
}: {
    warehouse: string;
    field: TimerMapField;
    knownKeys: readonly string[];
    initial: Record<string, number>;
    getPlaceholder: (key: string) => number;
    onSaved: (next: Record<string, number>) => void;
}) => {
    const { t } = useTranslation();
    const readOnly = useReadOnly();
    // String buffer so the user can clear the input without it snapping to 0.
    const buildBuffer = (src: Record<string, number>): Record<string, string> => {
        const out: Record<string, string> = {};
        [...knownKeys, ...Object.keys(src)].forEach(key => {
            out[key] = src[key] !== undefined ? String(src[key]) : "";
        });
        return out;
    };

    const [buffer, setBuffer] = useState<Record<string, string>>(() => buildBuffer(initial));
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        setBuffer(buildBuffer(initial));
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initial]);

    const dirty = useMemo(() => {
        const keys = new Set([...Object.keys(buffer), ...Object.keys(initial)]);
        for (const k of keys) {
            const raw = buffer[k];
            const n = raw === "" ? undefined : Number(raw);
            if (n !== initial[k]) return true;
        }
        return false;
    }, [buffer, initial]);

    const save = async () => {
        const payload: Record<string, number> = {};
        for (const [key, raw] of Object.entries(buffer)) {
            if (raw === "") continue;
            const n = Number(raw);
            if (Number.isFinite(n) && n >= 0) payload[key] = Math.round(n);
        }

        setSaving(true);
        try {
            const res = await apiFetch(
                `/settings/${encodeURIComponent(warehouse)}`,
                {
                    method: "PATCH",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ [field]: payload }),
                },
            );
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const updated: TimerSettingsRow = await res.json();
            onSaved((updated[field] as Record<string, number>) ?? {});
        } catch {
            toast.error(t("server_error"), { toastId: `settings-${field}-save-err-${warehouse}` });
        } finally {
            setSaving(false);
        }
    };

    // Auto-save: debounced after typing stops, flushed on blur. Bails
    // when nothing is dirty, so tabbing through inputs without changes
    // doesn't trigger a network round-trip.
    const saveIfDirty = () => { if (dirty && !saving && !readOnly) save(); };
    const autosave = useDebouncedAutoSave(() => saveIfDirty());

    return (
        <div className="admin-settings-po-editor">
            <div className="admin-settings-po-grid">
                {Object.keys(buffer).sort().map(key => (
                    <label key={key} className="admin-settings-po-row">
                        <span className="admin-settings-po-label">{key}</span>
                        <input
                            className="admin-settings-po-input"
                            type="number"
                            inputMode="numeric"
                            min={0}
                            step={100}
                            placeholder={String(getPlaceholder(key))}
                            value={buffer[key]}
                            onChange={e => {
                                setBuffer(prev => ({ ...prev, [key]: e.target.value }));
                                autosave.schedule("map");
                            }}
                            onBlur={() => autosave.flush("map")}
                            disabled={saving || readOnly}
                            aria-disabled={readOnly}
                            title={readOnly ? t("read_only_mode") : undefined}
                        />
                        <span className="admin-settings-po-unit">ms</span>
                    </label>
                ))}
            </div>
            {saving && (
                <div className="admin-settings-saving-indicator">{t("saving")}</div>
            )}
        </div>
    );
};

export const ScalarTimerEditor = ({
    rows,
    field,
    placeholderMs,
    onSaved,
}: {
    rows: TimerSettingsRow[];
    field: ScalarTimerField;
    placeholderMs: number;
    onSaved: (warehouse: string, next: number | null) => void;
}) => {
    const { t } = useTranslation();
    const readOnly = useReadOnly();
    // One string buffer per warehouse.
    const buildBuffer = (src: TimerSettingsRow[]): Record<string, string> => {
        const out: Record<string, string> = {};
        src.forEach(r => {
            const v = r[field];
            out[r.warehouse] = v == null ? "" : String(v);
        });
        return out;
    };

    const [buffer, setBuffer] = useState<Record<string, string>>(() => buildBuffer(rows));
    const [savingFor, setSavingFor] = useState<string | null>(null);

    useEffect(() => {
        setBuffer(buildBuffer(rows));
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rows]);

    const isDirty = (warehouse: string) => {
        const row = rows.find(r => r.warehouse === warehouse);
        if (!row) return false;
        const raw = buffer[warehouse] ?? "";
        const saved = row[field];
        if (raw === "") return saved != null;
        const n = Number(raw);
        if (!Number.isFinite(n)) return false;
        return Math.round(n) !== saved;
    };

    const save = async (warehouse: string) => {
        const raw = buffer[warehouse] ?? "";
        let payload: number | null;
        if (raw === "") {
            payload = null;
        } else {
            const n = Number(raw);
            if (!Number.isFinite(n) || n < 0) return;
            payload = Math.round(n);
        }

        setSavingFor(warehouse);
        try {
            const res = await apiFetch(
                `/settings/${encodeURIComponent(warehouse)}`,
                {
                    method: "PATCH",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ [field]: payload }),
                },
            );
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const updated: TimerSettingsRow = await res.json();
            onSaved(warehouse, (updated[field] as number | null) ?? null);
        } catch {
            toast.error(t("server_error"), { toastId: `settings-${field}-save-err-${warehouse}` });
        } finally {
            setSavingFor(null);
        }
    };

    const saveIfDirty = (warehouse: string) => {
        if (savingFor === warehouse || readOnly) return;
        if (!isDirty(warehouse)) return;
        save(warehouse);
    };
    const autosave = useDebouncedAutoSave(saveIfDirty);

    return (
        <div className="admin-settings-table">
            {rows.map(r => (
                <div key={r.warehouse} className="admin-settings-row admin-settings-row--scalar-inline">
                    <div className="admin-settings-warehouse">{r.warehouse}</div>
                    <div className="admin-settings-scalar-control">
                        <input
                            className="admin-settings-po-input"
                            type="number"
                            inputMode="numeric"
                            min={0}
                            step={100}
                            placeholder={String(placeholderMs)}
                            value={buffer[r.warehouse] ?? ""}
                            onChange={e => {
                                setBuffer(prev => ({ ...prev, [r.warehouse]: e.target.value }));
                                autosave.schedule(r.warehouse);
                            }}
                            onBlur={() => autosave.flush(r.warehouse)}
                            disabled={savingFor === r.warehouse || readOnly}
                            aria-disabled={readOnly}
                            title={readOnly ? t("read_only_mode") : undefined}
                        />
                        <span className="admin-settings-po-unit">
                            {savingFor === r.warehouse ? t("saving") : "ms"}
                        </span>
                    </div>
                </div>
            ))}
            {rows.length === 0 && (
                <div className="admin-settings-empty">{t("no_warehouses")}</div>
            )}
        </div>
    );
};

// One individual setting inside a sub-section, collapsed by default.
//
// Every editor below renders a row PER WAREHOUSE, so a sub-section with
// seven settings across thirteen warehouses is ~90 rows of scrolling
// before you find the one you came for. Collapsing each setting turns the
// sub-section back into a scannable list of names, at the cost of one
// click to see a value. The hash-navigation effect opens every <details>
// ancestor of its target, so deep links from the Pages search still land
// on an expanded setting.
export const SettingBlock = ({
    title,
    children,
}: {
    title: React.ReactNode;
    children: React.ReactNode;
}) => (
    <details className="admin-settings-setting">
        <summary className="admin-settings-setting-summary">
            <h4 className="admin-settings-setting-title">{title}</h4>
        </summary>
        <div className="admin-settings-setting-body">{children}</div>
    </details>
);

export const TimerMapPerWarehouse = ({
    rows,
    field,
    keysFor,
    getPlaceholder,
    onSaved,
}: {
    rows: TimerSettingsRow[];
    field: TimerMapField;
    keysFor: (warehouse: string) => readonly string[];
    getPlaceholder: (warehouse: string, key: string) => number;
    onSaved: (warehouse: string, next: Record<string, number>) => void;
}) => {
    const { t } = useTranslation();
    return (
        <>
            {rows.map(r => (
                <details key={r.warehouse} className="admin-settings-po-warehouse">
                    <summary className="admin-settings-po-warehouse-summary">
                        <span className="admin-settings-po-warehouse-name">{r.warehouse}</span>
                        <span className="admin-settings-po-warehouse-count">
                            {Object.keys(r[field] ?? {}).length} {t("custom_short")}
                        </span>
                    </summary>
                    <TimerMapEditor
                        warehouse={r.warehouse}
                        field={field}
                        knownKeys={keysFor(r.warehouse)}
                        initial={r[field] ?? {}}
                        getPlaceholder={(key) => getPlaceholder(r.warehouse, key)}
                        onSaved={(next) => onSaved(r.warehouse, next)}
                    />
                </details>
            ))}
        </>
    );
};
